import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AbstractComponent, ChatAvailabilityEnum } from 'lib-trend-core';
import { combineLatest, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { presenceSelector, statusesSelector } from '../state/chat.selectors';
import { ChatStatusEntry } from '../state/chat.state';
import { toggleChatStatusDialog } from './chat-status-dialog.opener';
import { ChatPresenceView, resolvePresenceView } from './chat-status.util';
import { ChatDialogService } from '../services/chat-dialog.service';

/** How often the view re-evaluates the status expiry (AC-25 auto-clear). */
const EXPIRY_TICK_MS = 30_000;

/**
 * "Meu status" control (SPEC-2026-07-internal-chat, T-21 · RF-29 / AC-25) —
 * mounted in the `/chat` full-view header and in the bubble popup header.
 *
 * Renders my availability dot + custom status (emoji/text) and opens the status
 * picker. Availability comes from the store `statuses` map, which the
 * `chat:status:updated` broadcast keeps live (the company presence room includes
 * me) and the status dialog patches optimistically. A 30 s tick re-evaluates
 * `statusExpiresAt` so an expired status disappears on its own without waiting
 * for the next bootstrap.
 */
@Component({
  selector: 'chat-status-button',
  templateUrl: './chat-status-button.component.html',
  styleUrls: ['./chat-status-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatStatusButtonComponent extends AbstractComponent implements OnInit {

  /** Compact mode (bubble popup header): dot + emoji only, no label text. */
  @Input() compact = false;

  view: ChatPresenceView = resolvePresenceView(undefined, true);

  /** Composed on data arrival (never in a lifecycle hook) — availability + status. */
  tooltip = '';

  private status: ChatStatusEntry | undefined;
  private online = true;

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly dialog: ChatDialogService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(statusesSelector),
      this.store.select(presenceSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([statuses, presence]) => {
        const myId = this.getIDCurrentUser();
        this.status = statuses[myId];
        // I am looking at the app, so treat myself as connected unless presence
        // explicitly says otherwise — a manual BUSY/AWAY still wins in the view.
        this.online = presence[myId] ?? true;
        this.recompute();
      });

    interval(EXPIRY_TICK_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recompute());
  }

  get availabilityLabel(): string {
    return this.view.labelKey;
  }

  get isBusy(): boolean {
    return this.view.availability === ChatAvailabilityEnum.BUSY;
  }

  /** Toggle, not open: a second activation closes the picker (see the opener). */
  open(): void {
    toggleChatStatusDialog(this.dialog);
  }

  private recompute(): void {
    this.view = resolvePresenceView(this.status, this.online);
    const label = this.t(this.view.labelKey);
    this.tooltip = this.view.text
      ? `${label} · ${this.view.emoji ? `${this.view.emoji} ` : ''}${this.view.text}`
      : `${label} — ${this.t('chat.status.open')}`;
  }
}
